/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';

import { ClinicalDepartmentEnum } from '../models/clinical-department-enum';
import { CreateMetadataDraftRequest } from '../models/create-metadata-draft-request';
import { FieldMetadataResponse } from '../models/field-metadata-response';
import { MetadataDocumentResponse } from '../models/metadata-document-response';
import { MetadataFieldSearchResponse } from '../models/metadata-field-search-response';
import { MetadataKindEnum } from '../models/metadata-kind-enum';
import { MetadataVersionListResponse } from '../models/metadata-version-list-response';
import { MetadataVersionResponse } from '../models/metadata-version-response';

/**
 * Field metadata / future field-type APIs for frontend
 */
@Injectable({ providedIn: 'root' })
export class FormMetadataService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `listGlobalFieldTypes()` */
  static readonly ListGlobalFieldTypesPath = '/api/v1/forms/field-types';

  /**
   * Field-type / metadata placeholder (frontend contract TBD).
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `listGlobalFieldTypes()` instead.
   *
   * This method doesn't expect any request body.
   */
  listGlobalFieldTypes$Response(
    params?: {},
    context?: HttpContext
  ): Observable<StrictHttpResponse<FieldMetadataResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      FormMetadataService.ListGlobalFieldTypesPath,
      'get'
    );
    if (params) {
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<FieldMetadataResponse>;
        })
      );
  }

  /**
   * Field-type / metadata placeholder (frontend contract TBD).
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `listGlobalFieldTypes$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  listGlobalFieldTypes(
    params?: {},
    context?: HttpContext
  ): Observable<FieldMetadataResponse> {
    return this.listGlobalFieldTypes$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<FieldMetadataResponse>): FieldMetadataResponse =>
          r.body
      )
    );
  }

  /** Path part for operation `listDepartmentFieldTypes()` */
  static readonly ListDepartmentFieldTypesPath =
    '/api/v1/forms/{department}/field-types';

  /**
   * Field-type / metadata by department (ROTOR or FIXED).
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `listDepartmentFieldTypes()` instead.
   *
   * This method doesn't expect any request body.
   */
  listDepartmentFieldTypes$Response(
    params: {
      department: ClinicalDepartmentEnum;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<FieldMetadataResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      FormMetadataService.ListDepartmentFieldTypesPath,
      'get'
    );
    if (params) {
      rb.path('department', params.department, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<FieldMetadataResponse>;
        })
      );
  }

  /**
   * Field-type / metadata by department (ROTOR or FIXED).
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `listDepartmentFieldTypes$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  listDepartmentFieldTypes(
    params: {
      department: ClinicalDepartmentEnum;
    },
    context?: HttpContext
  ): Observable<FieldMetadataResponse> {
    return this.listDepartmentFieldTypes$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<FieldMetadataResponse>): FieldMetadataResponse =>
          r.body
      )
    );
  }

  /** Path part for operation `listMetadataVersions()` */
  static readonly ListMetadataVersionsPath =
    '/api/v1/clinical/metadata/admin/versions';

  /**
   * List all versions of a metadata document kind (admin).
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `listMetadataVersions()` instead.
   *
   * This method doesn't expect any request body.
   */
  listMetadataVersions$Response(
    params: {
      kind: MetadataKindEnum;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<MetadataVersionListResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      FormMetadataService.ListMetadataVersionsPath,
      'get'
    );
    if (params) {
      rb.query('kind', params.kind, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<MetadataVersionListResponse>;
        })
      );
  }

  /**
   * List all versions of a metadata document kind (admin).
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `listMetadataVersions$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  listMetadataVersions(
    params: {
      kind: MetadataKindEnum;
    },
    context?: HttpContext
  ): Observable<MetadataVersionListResponse> {
    return this.listMetadataVersions$Response(params, context).pipe(
      map(
        (
          r: StrictHttpResponse<MetadataVersionListResponse>
        ): MetadataVersionListResponse => r.body
      )
    );
  }

  /** Path part for operation `getMetadataContent()` */
  static readonly GetMetadataContentPath =
    '/api/v1/clinical/metadata/admin/content';

  /**
   * Get metadata document content (active published by default, or a specific version).
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getMetadataContent()` instead.
   *
   * This method doesn't expect any request body.
   */
  getMetadataContent$Response(
    params: {
      kind: MetadataKindEnum;
      version?: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<MetadataDocumentResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      FormMetadataService.GetMetadataContentPath,
      'get'
    );
    if (params) {
      rb.query('kind', params.kind, {});
      rb.query('version', params.version, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<MetadataDocumentResponse>;
        })
      );
  }

  /**
   * Get metadata document content (active published by default, or a specific version).
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getMetadataContent$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getMetadataContent(
    params: {
      kind: MetadataKindEnum;
      version?: number;
    },
    context?: HttpContext
  ): Observable<MetadataDocumentResponse> {
    return this.getMetadataContent$Response(params, context).pipe(
      map(
        (
          r: StrictHttpResponse<MetadataDocumentResponse>
        ): MetadataDocumentResponse => r.body
      )
    );
  }

  /** Path part for operation `createMetadataDraft()` */
  static readonly CreateMetadataDraftPath =
    '/api/v1/clinical/metadata/admin/draft';

  /**
   * Create a new DRAFT version of a metadata document (admin).
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createMetadataDraft()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createMetadataDraft$Response(
    params: {
      kind: MetadataKindEnum;
      body: CreateMetadataDraftRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<MetadataVersionResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      FormMetadataService.CreateMetadataDraftPath,
      'post'
    );
    if (params) {
      rb.query('kind', params.kind, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<MetadataVersionResponse>;
        })
      );
  }

  /**
   * Create a new DRAFT version of a metadata document (admin).
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createMetadataDraft$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createMetadataDraft(
    params: {
      kind: MetadataKindEnum;
      body: CreateMetadataDraftRequest;
    },
    context?: HttpContext
  ): Observable<MetadataVersionResponse> {
    return this.createMetadataDraft$Response(params, context).pipe(
      map(
        (
          r: StrictHttpResponse<MetadataVersionResponse>
        ): MetadataVersionResponse => r.body
      )
    );
  }

  /** Path part for operation `publishMetadataVersion()` */
  static readonly PublishMetadataVersionPath =
    '/api/v1/clinical/metadata/admin/versions/{version}/publish';

  /**
   * Publish a metadata version (archives the currently published version).
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `publishMetadataVersion()` instead.
   *
   * This method doesn't expect any request body.
   */
  publishMetadataVersion$Response(
    params: {
      version: number;
      kind: MetadataKindEnum;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<MetadataVersionResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      FormMetadataService.PublishMetadataVersionPath,
      'post'
    );
    if (params) {
      rb.path('version', params.version, {});
      rb.query('kind', params.kind, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<MetadataVersionResponse>;
        })
      );
  }

  /**
   * Publish a metadata version (archives the currently published version).
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `publishMetadataVersion$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  publishMetadataVersion(
    params: {
      version: number;
      kind: MetadataKindEnum;
    },
    context?: HttpContext
  ): Observable<MetadataVersionResponse> {
    return this.publishMetadataVersion$Response(params, context).pipe(
      map(
        (
          r: StrictHttpResponse<MetadataVersionResponse>
        ): MetadataVersionResponse => r.body
      )
    );
  }

  /** Path part for operation `searchMetadataFields()` */
  static readonly SearchMetadataFieldsPath =
    '/api/v1/clinical/metadata/admin/fields/search';

  /**
   * Search the published FIELD_METADATA field index (admin).
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `searchMetadataFields()` instead.
   *
   * This method doesn't expect any request body.
   */
  searchMetadataFields$Response(
    params?: {
      department?: string;
      formType?: string;
      term?: string;
      page?: number;
      size?: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<MetadataFieldSearchResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      FormMetadataService.SearchMetadataFieldsPath,
      'get'
    );
    if (params) {
      rb.query('department', params.department, {});
      rb.query('formType', params.formType, {});
      rb.query('term', params.term, {});
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<MetadataFieldSearchResponse>;
        })
      );
  }

  /**
   * Search the published FIELD_METADATA field index (admin).
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `searchMetadataFields$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  searchMetadataFields(
    params?: {
      department?: string;
      formType?: string;
      term?: string;
      page?: number;
      size?: number;
    },
    context?: HttpContext
  ): Observable<MetadataFieldSearchResponse> {
    return this.searchMetadataFields$Response(params, context).pipe(
      map(
        (
          r: StrictHttpResponse<MetadataFieldSearchResponse>
        ): MetadataFieldSearchResponse => r.body
      )
    );
  }
}
